import type WebModel from '../models/WebModel'
import jump from 'jump.js'
import type SlotFactory from '../slots/SlotFactory'
import type PrebidFacade from '../prebidFacade/PrebidFacade'
import SlotRepository from '../slots/SlotRepository'
import type { Slot } from '../slots/Slot'
import { Arrival } from '../slots/Arrival'
import { ExecutionStatus } from '../wrapper'
import SlotCounter from '../slots/SlotCounter'
import { getAdBoxBySlot } from '../slots/helpers/AdBox'

const continueButtonSelector = '.mv-arrival-continue-button'

const RENDER_BUFFER = 150

export class ArrivalSlot {
  static jtrClicked: boolean = false
  static arrivalSlot: Arrival | undefined | null
  static readonly jtrSelector: string =
    '.tasty-recipes-jump-link, .wprm-recipe-jump'

  constructor(
    readonly model: WebModel,
    readonly slotFactory: SlotFactory,
    readonly prebidFacade: PrebidFacade
  ) {}

  /**
   * The Arrival slot listener has to be set up before
   * the main load function is called. Otherwise, it would
   * not get triggered when a site has pagespeed optimize on
   * AND the user didn't scroll before clicking.
   * So we use a flag, jtrClicked to indicate to the render
   * func if it needs to proceed with rendering.
   */
  render() {
    if (
      ArrivalSlot.jtrClicked &&
      !ArrivalSlot.arrivalSlot &&
      this.model.jtr_arrival_unit_enabled
    ) {
      const target = ArrivalSlot.getJtrButton(this.model)
      if (!target) {
        return
      }
      this.onJTRClick(target as HTMLElement)
    }
  }

  static getJtrButton(model: WebModel) {
    const jtrSelector = model.jtr_button_selector || ArrivalSlot.jtrSelector
    const target = document.querySelector(jtrSelector)

    return target
  }

  /** The spot we want to stick the Arrival Unit. */
  static getLocation(model: WebModel) {
    const target =
      model.jtr_selector ||
      ArrivalSlot.getJtrButton(model)?.getAttribute('href') ||
      ''
    const location = document.querySelector(target) as HTMLElement

    return location
  }

  static attachListener(
    model: WebModel,
    loadFunc: () => Promise<ExecutionStatus>
  ) {
    if (!model.jtr_arrival_unit_enabled) {
      return
    }

    const target = ArrivalSlot.getJtrButton(model)

    if (!target) {
      return
    }

    const callback = async (e: Event) => {
      e.preventDefault()
      e.stopPropagation()

      if (this.jtrClicked) {
        ArrivalSlot.scrollToArrivalSlot()
      }

      this.jtrClicked = true
      const loadStatus = await loadFunc()

      // If the wrapper is halted, we'll want to trigger the default JTR button action.
      if (loadStatus === ExecutionStatus.halted) {
        const click = new MouseEvent(e.type, e)
        target.removeEventListener('click', callback)
        target.dispatchEvent(click)
      }
    }

    target.addEventListener('click', callback)
    // target.addEventListener('touchstart', callback)
  }

  async onJTRClick(target: HTMLElement) {
    const location = ArrivalSlot.getLocation(this.model)
    if (!location) {
      return
    }

    if (!ArrivalSlot.arrivalSlot) {
      target as HTMLButtonElement
      const newSlot = await this.createArrivalSlot(location)
      if (newSlot) {
        ArrivalSlot.arrivalSlot = newSlot
        this.addContinueButtonListener(location)
        this.moveTastyPrintButton(ArrivalSlot.arrivalSlot)
        this.auctionSlots()
        deleteLastContentSlot()
        setTimeout(() => ArrivalSlot.scrollToArrivalSlot(), RENDER_BUFFER)
      }
    }
  }

  static scrollToArrivalSlot() {
    const offsetForContinueButton = -160
    const jumpDuration = 3000
    const secondJumpDuration = 500
    const wrapperDiv = ArrivalSlot?.arrivalSlot?.wrapper

    // Sometimes, because of AdBox/CLS, the wrapper will be display:none,
    // and we can't jump to elements that aren't displayed.
    // In that case, we jump to the bottom of the element above the wrapper.
    const targetDestination =
      wrapperDiv && wrapperDiv.style.display !== 'none'
        ? wrapperDiv.getBoundingClientRect().top
        : wrapperDiv?.previousElementSibling?.getBoundingClientRect().bottom

    const callback = () => {
      jump(ArrivalSlot.arrivalSlot?.wrapper, {
        duration: secondJumpDuration,
        easing: easeInOutQuad,
        offset: offsetForContinueButton,
        callback: () => window.$mediavine.web.setContentWaypointDisable(false)
      })
    }

    // This chunk handles that case of WPRocket being on the page: https://github.com/mediavine/web-wrapper/pull/1519
    if (window.LazyLoad && ArrivalSlot.arrivalSlot) {
      scrollTo({ top: targetDestination })
      callback()

      return
    }

    window.$mediavine.web.setContentWaypointDisable(true)

    /**
     * This double jump gives a fancy little
     * bounce back effect.
     */
    jump(targetDestination, {
      duration: jumpDuration,
      callback
    })
  }

  /**
   * Arrival slot has a little "Continue To Content" button.  This
   * listener takes us to the recipe card when it gets clicked.
   */

  private addContinueButtonListener(target: HTMLElement) {
    const continueButton = document.querySelector(continueButtonSelector)
    if (!continueButton) {
      return
    }
    continueButton.addEventListener('click', (e) => {
      e.preventDefault()
      jump(target, {
        duration: 500,
        offset: 300,
        easing: easeInOutQuad
      })
    })
  }

  private moveTastyPrintButton(slot: Slot | undefined) {
    if (!slot) {
      return
    }
    const printButton = document.querySelector('.tasty-recipes-print-button')
    if (!printButton) {
      return
    }
    printButton.remove()
    slot.getSlotDom().insertAdjacentElement('afterend', printButton)
  }

  /** Auction our arrival slot plus any Recipe slots while we travel down the page */
  private auctionSlots() {
    if (!ArrivalSlot.arrivalSlot) {
      return
    }
    const recipeSlots = SlotRepository.getRecipeSlots().filter((s) => !s.adType)
    this.prebidFacade.requestBids(
      [ArrivalSlot.arrivalSlot, ...recipeSlots],
      ArrivalSlot.arrivalSlot.timeout.value
    )
  }

  private async createArrivalSlot(
    location: HTMLElement
  ): Promise<Arrival | undefined> {
    return (await this.slotFactory.new({
      adUnitId: 'arrival',
      insertPosition: 'beforebegin',
      selector: location,
      type: 'Arrival'
    })) as Arrival
  }
}

const easeInOutQuad = (t, b, c, d) => {
  t /= d / 2
  if (t < 1) return (c / 2) * t * t + b
  t--
  return (-c / 2) * (t * (t - 2) - 1) + b
}

/**
 * Some publishers don't like that a content slot can be directly above
 * an arrival unit. This little diddy just blows up the last content slot
 * if JTR is clicked.
 */
function deleteLastContentSlot() {
  /**
   * SlotCounter.current is designed to assign the next slot's number.
   * If we want the last rendered, we need to subtract 1.
   */
  const lastContentSlotNumber = SlotCounter.current('Content') - 1
  const desktopId = `content_${lastContentSlotNumber}_btf`
  const mobileId = `content_${lastContentSlotNumber}_mobile`
  const lastContentSlot =
    SlotRepository.getSlotById(desktopId) ||
    SlotRepository.getSlotById(mobileId)
  if (!lastContentSlot) {
    return
  }
  const adBox = getAdBoxBySlot(lastContentSlot)
  adBox ? adBox.remove() : lastContentSlot.wrapper.remove()
}
